import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useSEO, articleLd, breadcrumbLd } from "@/lib/seo";
import BookButton from "@/components/BookButton";
import { PROPERTY } from "@/lib/constants";

function renderBody(body) {
  // Lightweight markdown: ## headings, ** bold, [text](url) links, paragraphs.
  // Bold may wrap a link (e.g. **[text](/url)**) — handled by recursing into inline().
  const blocks = body.split(/\n\n+/).map((b) => b.trim()).filter(Boolean);
  const inline = (text) =>
    text
      .split(/(\[[^\]]+\]\([^)]+\)|\*\*[^*]+(?:\*(?!\*)[^*]+)*\*\*)/g)
      .map((seg, i) => {
        const link = seg.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (link) {
          const [, label, href] = link;
          if (href.startsWith("/"))
            return <Link key={i} to={href} className="text-ember underline underline-offset-4 decoration-1 hover:text-ember-hover">{label}</Link>;
          if (!/^(https?:|mailto:|tel:)/i.test(href)) return <span key={i}>{label}</span>;
          return <a key={i} href={href} className="text-ember underline underline-offset-4 decoration-1 hover:text-ember-hover" target="_blank" rel="noopener noreferrer">{label}</a>;
        }
        const bold = seg.match(/^\*\*([\s\S]+)\*\*$/);
        if (bold) return <strong key={i} className="text-ink font-semibold">{inline(bold[1])}</strong>;
        return <span key={i}>{seg}</span>;
      });

  return blocks.map((block, i) => {
    if (block.startsWith("## "))
      return <h2 key={i} className="font-display text-3xl md:text-4xl text-ink mt-12 mb-4">{block.slice(3)}</h2>;
    if (block.startsWith("- ")) {
      const items = block.split("\n").map((l) => l.replace(/^- /, ""));
      return (
        <ul key={i} className="space-y-3 my-6">
          {items.map((it, j) => (
            <li key={j} className="flex gap-4 text-ink-soft leading-relaxed">
              <span className="text-ember mt-1.5 flex-shrink-0">→</span>
              <span>{inline(it)}</span>
            </li>
          ))}
        </ul>
      );
    }
    return <p key={i} className="text-ink-soft leading-relaxed text-lg my-4">{inline(block)}</p>;
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(null));
    api.get(`/blog`).then((r) => setRelated(r.data.filter((p) => p.slug !== slug).slice(0, 3))).catch(() => {});
  }, [slug]);

  useSEO(
    post
      ? {
          title: post.title,
          description: post.meta_description || post.excerpt,
          path: `/blog/${post.slug}`,
          image: post.hero_image,
          jsonLd: [
            articleLd({
              title: post.title,
              description: post.meta_description || post.excerpt,
              image: post.hero_image,
              slug: post.slug,
              datePublished: post.published_at,
            }),
            breadcrumbLd([
              { name: "Home", path: "/" },
              { name: "Journal", path: "/blog" },
              { name: post.title, path: `/blog/${post.slug}` },
            ]),
          ],
        }
      : {}
  );

  if (post === undefined) {
    return <div className="pt-36 min-h-[60vh] text-center text-ink-muted">Loading…</div>;
  }
  if (post === null) return <Navigate to="/blog" replace />;

  return (
    <div data-testid={`blog-post-${post.slug}`}>
      <section className="pt-36 pb-12 bg-canvas">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <Link to="/blog" className="font-mono-label text-ink-muted hover:text-ember link-underline">← Journal</Link>
          <p className="font-mono-label text-ember mt-8 mb-4">
            {post.category} · {new Date(post.published_at).toLocaleDateString("en-US", { month: "long", year: "numeric" })}
          </p>
          <h1 className="font-display text-5xl md:text-6xl text-ink leading-[1.02]">{post.title}</h1>
          <p className="mt-6 text-xl text-ink-soft leading-relaxed">{post.excerpt}</p>
        </div>
      </section>

      <section className="bg-canvas">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="aspect-[16/9] overflow-hidden bg-dune">
            <img src={post.hero_image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <article className="bg-canvas py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          {renderBody(post.body)}

          {/* In-article CTA */}
          <aside className="mt-16 bg-sand p-8 md:p-12 border-l-2 border-ember" data-testid="article-cta">
            <p className="font-mono-label text-ember mb-3">Plan your stay</p>
            <p className="font-display text-3xl md:text-4xl text-ink leading-tight">
              Reserve a room at Westbridge Inn &amp; Suites.
            </p>
            <p className="mt-4 text-ink-soft">1408 N US-65, Carrollton MO · {PROPERTY.phone} · 24-hour front desk</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <BookButton label="Book Direct & Save" testid={`article-book-${post.slug}`} />
              <Link to="/contact" className="btn-ink">Send an inquiry</Link>
            </div>
          </aside>

          {/* Keywords / topic tags */}
          {post.keywords?.length > 0 && (
            <div className="mt-16 pt-8 border-t border-rule-subtle">
              <p className="font-mono-label text-ink-muted mb-4">Topics</p>
              <div className="flex flex-wrap gap-2">
                {post.keywords.map((k) => (
                  <span key={k} className="text-xs font-mono-label text-ink-soft bg-sand px-3 py-1.5">{k}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-sand py-20" data-testid="related-section">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <h2 className="font-display text-4xl md:text-5xl text-ink mb-10">Keep reading</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {related.map((r) => (
                <Link key={r.id} to={`/blog/${r.slug}`} className="group block" data-testid={`related-${r.slug}`}>
                  <div className="aspect-[4/3] overflow-hidden bg-dune mb-4">
                    <img src={r.hero_image} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" loading="lazy" />
                  </div>
                  <p className="font-mono-label text-ember">{r.category}</p>
                  <h3 className="font-display text-2xl text-ink mt-2 leading-tight">{r.title}</h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
